import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Globe, ExternalLink, Map, Check } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import { categoryLabel } from "../components/PlaceCard";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import SEO, { placeLD, breadcrumbLD } from "../components/SEO";
import GoogleRatingBadge from "../components/GoogleRatingBadge";

export default function AdresseDetail() {
    const { slug } = useParams();
    const [place, setPlace] = useState(null);
    const [city, setCity] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setPlace(null); setCity(null); setError("");
        api.get(`/places/${slug}`).then((r) => {
            setPlace(r.data);
            if (r.data.city_slug) {
                api.get(`/cities/${r.data.city_slug}`).then((c) => setCity(c.data)).catch(() => {});
            }
        }).catch(() => setError("Adresse introuvable."));
    }, [slug]);

    if (error) return (
        <div className="pt-40 px-6 text-center pb-40">
            <p className="cp-kicker text-cp-foret">Oups</p>
            <h1 className="cp-serif text-5xl mt-4">{error}</h1>
            <Link to="/adresses" className="cp-btn-ghost mt-10 inline-flex">Retour</Link>
        </div>
    );
    if (!place) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;

    const officialSite = place.website;
    const sourceUrl = place.source_url;
    const sourceLabel = sourceUrl?.includes('tripadvisor') ? 'Tripadvisor' :
                        sourceUrl?.includes('petitfute') ? 'Petit Futé' :
                        sourceUrl?.includes('michelin') ? 'Michelin' :
                        sourceUrl?.includes('logishotels') ? 'Logis Hôtels' :
                        'Source officielle';
    const mapsUrl = place.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place.name} ${place.address}`)}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place.name} ${place.city_slug || ""}`)}`;
    const cityLabel = city?.name || (place.city_slug || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

    return (
        <div data-testid={`adresse-detail-${slug}`}>
            <SEO
                title={`${place.name} · ${cityLabel}`}
                description={place.short_description || `${place.name} à ${cityLabel} — ${categoryLabel(place.category)} vérifié sur Côte Perdue (note Google ${place.google_rating || "—"}/5).`}
                image={place.image}
                path={`/adresses/${slug}`}
                type="article"
                keywords={[
                    place.name, cityLabel, categoryLabel(place.category),
                    "Normandie", "Picardie", "Côte Perdue",
                    ...(place.tags || []),
                ]}
                structuredData={[
                    placeLD(place),
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Adresses", path: "/adresses" },
                        { name: place.name, path: `/adresses/${slug}` },
                    ]),
                ]}
            />
            <section className="relative min-h-[65vh] md:min-h-[75vh] overflow-hidden bg-cp-encre cp-photo-frame">
                {place.image ? (
                    <img src={resolveImg(place.image)} alt={place.name} className="absolute inset-0 w-full h-full object-cover cp-kenburns cp-photo-real-dark" />
                ) : (
                    <PhotoPlaceholder name={place.name} category={place.category} size="large" />
                )}
                <div className="absolute inset-0 cp-hero-overlay" />
                <div className="relative z-10 flex flex-col justify-end min-h-[65vh] md:min-h-[75vh] px-6 md:px-12 py-20 max-w-[1600px] mx-auto">
                    <Link to="/adresses" className="cp-kicker text-cp-ivoire/70 hover:text-cp-ivoire transition-colors flex items-center gap-2 mb-8">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Adresses
                    </Link>
                    <p className="cp-kicker text-cp-sable mb-4">{categoryLabel(place.category)} · {cityLabel}</p>
                    <h1 className="cp-serif text-cp-ivoire text-3xl sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[72px] leading-[1.05] tracking-tight max-w-4xl">
                        {place.name}
                    </h1>
                    <p className="text-cp-ivoire/90 text-lg md:text-2xl italic font-light mt-6 max-w-2xl">{place.short_description}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-6">
                        {place.google_rating && (
                            <GoogleRatingBadge
                                rating={place.google_rating}
                                count={place.google_review_count}
                                placeId={place.google_place_id}
                                variant="dark"
                            />
                        )}
                        {place.source_url && (
                            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cp-sable border border-cp-sable/40 px-3 py-1.5" data-testid="verified-source-badge">
                                <Check size={12} strokeWidth={2} /> Source vérifiée · Tripadvisor / Petit Futé
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* RESERVATION CTA BAR */}
            <section className="bg-cp-foret text-cp-ivoire py-6 md:py-7" data-testid="reservation-bar">
                <div className="px-6 md:px-12 max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                    <div className="flex-1">
                        <p className="cp-kicker text-[10px] text-cp-sable mb-1">{officialSite ? 'Réserver cette adresse' : 'Découvrir cette adresse'}</p>
                        <p className="cp-serif text-xl md:text-2xl italic">
                            {officialSite ? "Toujours via le site officiel de l'établissement." : `Avis et infos vérifiés sur ${sourceLabel}.`}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {officialSite && (
                            <a
                                href={officialSite}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="reserve-official-btn"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-cp-sable text-cp-encre border border-cp-sable hover:bg-cp-ivoire transition-colors cp-kicker text-[11px]"
                            >
                                Réserver sur le site officiel <ExternalLink size={14} strokeWidth={1.5} />
                            </a>
                        )}
                        {sourceUrl && !officialSite && (
                            <a
                                href={sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="source-btn"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-cp-sable text-cp-encre border border-cp-sable hover:bg-cp-ivoire transition-colors cp-kicker text-[11px]"
                            >
                                Voir sur {sourceLabel} <ExternalLink size={14} strokeWidth={1.5} />
                            </a>
                        )}
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="maps-btn"
                            className="inline-flex items-center gap-2 px-6 py-3 border border-cp-ivoire/60 text-cp-ivoire hover:bg-cp-ivoire/10 transition-colors cp-kicker text-[11px]"
                        >
                            Itinéraire Google Maps <Map size={14} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
                <aside className="md:col-span-4 flex flex-col gap-6">
                    {place.address && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">Adresse</p>
                            <p className="flex items-start gap-2 text-cp-noir"><MapPin size={16} strokeWidth={1.5} className="mt-1 shrink-0" /> {place.address}</p>
                        </div>
                    )}
                    {place.phone && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">Téléphone</p>
                            <p className="flex items-center gap-2 text-cp-noir"><Phone size={16} strokeWidth={1.5} /> {place.phone}</p>
                        </div>
                    )}
                    {(officialSite || sourceUrl) && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">{officialSite ? 'Site officiel' : sourceLabel}</p>
                            <a href={officialSite || sourceUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cp-foret hover:text-cp-noir break-all">
                                <Globe size={16} strokeWidth={1.5} className="shrink-0" /> {(officialSite || sourceUrl).replace(/^https?:\/\//, "").slice(0, 40)}…
                            </a>
                        </div>
                    )}
                    {place.price_range && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">Gamme</p>
                            <p className="cp-serif text-2xl">{place.price_range}</p>
                        </div>
                    )}
                    {place.tags?.length > 0 && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">Ambiance</p>
                            <div className="flex flex-wrap gap-2">
                                {place.tags.map((t) => (
                                    <span key={t} className="cp-kicker text-[10px] border border-cp-border px-3 py-1.5 text-cp-noir-doux">{t}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
                <div className="md:col-span-8">
                    <p className="text-cp-noir text-xl md:text-2xl font-light leading-relaxed whitespace-pre-line">{place.description}</p>
                    {place.gallery?.length > 0 && (
                        <div className="grid grid-cols-2 gap-4 mt-12">
                            {place.gallery.map((img, i) => (
                                <div key={i} className="aspect-[4/5] overflow-hidden bg-cp-craie">
                                    <img src={resolveImg(img)} alt="" loading="lazy" className="w-full h-full object-cover cp-photo-real" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
